import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  finishBootstrap,
  listFleet,
  restartBrowser,
  startBootstrap,
  type FleetRow,
} from "@/services/browserFarmApi";
import VncViewer from "./VncViewer";

/**
 * Flotte de navigateurs serveur.
 *
 * Une ligne par compte LinkedIn exécuté côté serveur. Le pod qui la sert peut
 * changer d'un redémarrage à l'autre : c'est le bail qui fait foi, pas
 * l'ordinal du StatefulSet, et la colonne « pod » n'est là que pour aller lire
 * les bons logs.
 */

const STATE_STYLES: Record<string, string> = {
  running: "bg-green-100 text-green-800",
  bootstrapping: "bg-blue-100 text-blue-800",
  idle: "bg-gray-100 text-gray-700",
  unassigned: "bg-amber-100 text-amber-800",
  stale: "bg-red-100 text-red-800",
  degraded: "bg-orange-100 text-orange-800",
};

const STATE_HELP: Record<string, string> = {
  // Deux pannes différentes, deux gestes différents — c'est tout l'intérêt de
  // les distinguer plutôt que d'afficher « KO ».
  unassigned:
    "Compte configuré, aucun pod libre pour le tenir. Le geste est d'augmenter replicaCount, pas de redémarrer.",
  stale:
    "Un pod tient ce compte mais ne bat plus. Lire les logs de ce pod : un lancement de Chrome qui ne rend jamais la main laisse exactement cette trace.",
  degraded:
    "Le pod bat mais l'extension ne travaille pas (voir la raison) : ouvrir la vue noVNC, vérifier la session LinkedIn et la console.",
};

function formatAge(iso?: string | null): string {
  if (!iso) return "-";
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 90) return `il y a ${Math.round(seconds)} s`;
  if (seconds < 5400) return `il y a ${Math.round(seconds / 60)} min`;
  return `il y a ${Math.round(seconds / 3600)} h`;
}

export default function BrowserFarmPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [rows, setRows] = useState<FleetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<number | null>(null);

  const reload = useCallback(async () => {
    try {
      setRows(await listFleet(navigate));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    void reload();
    // Le heartbeat des pods est à 30 s : rafraîchir plus vite n'apprendrait
    // rien de plus et ferait clignoter la table.
    const id = window.setInterval(() => void reload(), 30_000);
    return () => window.clearInterval(id);
  }, [reload]);

  async function act(
    id: number,
    fn: (id: number, nav: typeof navigate) => Promise<{ detail?: string | null }>,
    title: string,
  ) {
    setBusy(id);
    try {
      const out = await fn(id, navigate);
      toast({ title, description: out.detail ?? undefined });
      await reload();
    } catch (e) {
      toast({
        title: `${title} — échec`,
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBusy(null);
    }
  }

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-semibold">Navigateurs serveur</h1>
          </div>
        </header>

        <div className="space-y-6 p-6">
          <p className="max-w-3xl text-sm text-gray-600">
            Un pod = un compte LinkedIn = un Chrome réel avec l&apos;extension RecrutAuto chargée,
            derrière le proxy de ce compte. L&apos;extension y fait exactement ce qu&apos;elle fait
            dans le navigateur d&apos;un recruteur. Pour ajouter un compte : configurez son proxy
            et passez-le en mode « serveur » depuis sa fiche d&apos;abonnement, puis connectez-le
            une fois via noVNC.
          </p>

          {error && <div className="text-sm text-red-600">{error}</div>}
          {loading && <div className="text-sm text-gray-500">Chargement...</div>}

          {!loading && rows.length === 0 && (
            <div className="rounded border border-dashed p-6 text-sm text-gray-500">
              Aucun compte en mode serveur. La flotte se remplit depuis les fiches
              d&apos;abonnement — panneau « Compte d&apos;exécution ».
            </div>
          )}

          {rows.length > 0 && (
            <div className="overflow-auto rounded border">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Compte</th>
                    <th className="px-3 py-2 text-left">État</th>
                    <th className="px-3 py-2 text-left">Pod</th>
                    <th className="px-3 py-2 text-left">Dernier battement</th>
                    <th className="px-3 py-2 text-left">Sortie</th>
                    <th className="px-3 py-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.subscription_id} className="border-t align-top">
                      <td className="px-3 py-2">
                        <div className="font-mono text-xs">#{r.subscription_id}</div>
                        {r.user_id && (
                          <Link
                            to={`/subscriptions/${r.user_id}`}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            {r.user_id.substring(0, 14)}...
                          </Link>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`rounded px-2 py-0.5 text-xs font-medium ${
                            STATE_STYLES[r.state] ?? "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {r.state}
                        </span>
                        {r.degraded_reason && (
                          <div className="mt-1 text-xs font-medium text-orange-800">
                            {r.degraded_reason}
                          </div>
                        )}
                        {STATE_HELP[r.state] && (
                          <div className="mt-1 max-w-xs text-xs text-gray-500">
                            {STATE_HELP[r.state]}
                          </div>
                        )}
                        {r.challenged_at && (
                          <div className="mt-1 rounded bg-red-100 px-2 py-0.5 text-xs text-red-800">
                            Challenge {r.challenge_kind ?? ""} — quarantaine
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{r.pod_hostname ?? "-"}</td>
                      <td className="px-3 py-2 text-xs">{formatAge(r.heartbeat_at)}</td>
                      <td className="px-3 py-2 text-xs">
                        {r.proxy_endpoint ?? "-"}
                        {r.proxy_country ? ` (${r.proxy_country})` : ""}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!r.pod_hostname || busy === r.subscription_id}
                            onClick={() => setViewing(r.subscription_id)}
                          >
                            Écran
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!r.pod_hostname || busy === r.subscription_id}
                            onClick={() =>
                              act(r.subscription_id, startBootstrap, "Session de connexion ouverte")
                            }
                            title="Efface le profil et rouvre un Chrome propre pour une connexion humaine"
                          >
                            Reconnecter
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={!r.pod_hostname || busy === r.subscription_id}
                            onClick={() =>
                              act(r.subscription_id, finishBootstrap, "Session enregistrée")
                            }
                            title="À cliquer une fois la connexion faite dans l'écran"
                          >
                            Enregistrer
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={!r.pod_hostname || busy === r.subscription_id}
                            onClick={() =>
                              act(r.subscription_id, restartBrowser, "Chrome relancé")
                            }
                          >
                            Relancer
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {viewing !== null && (
            <section>
              <h2 className="mb-3 text-lg font-semibold">Écran du compte #{viewing}</h2>
              <VncViewer subscriptionId={viewing} onClose={() => setViewing(null)} />
            </section>
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
